/**
 * ⚡ SPACE CONTENT - Module Gestion de Contenu
 * Offres flash, Sondages, Actualités, Événements
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, BarChart3, Newspaper, Calendar, Plus, Trash2,
  Loader2, RefreshCw, Gift, MessageSquare, Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Carte de contenu
const ContentCard = ({ item, type, onDelete }) => {
  const typeConfig = {
    flash: { color: 'yellow', icon: Zap },
    poll: { color: 'purple', icon: BarChart3 },
    news: { color: 'cyan', icon: Newspaper },
    event: { color: 'pink', icon: Calendar },
  };
  
  const config = typeConfig[type] || typeConfig.flash;
  const colorClasses = {
    yellow: 'border-yellow-500/30 bg-yellow-500/10',
    purple: 'border-purple-500/30 bg-purple-500/10',
    cyan: 'border-cyan-500/30 bg-cyan-500/10',
    pink: 'border-pink-500/30 bg-pink-500/10',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-xl border ${colorClasses[config.color]}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-white font-semibold text-sm">
            {item.emoji} {item.title || item.question}
          </p>
          {type === 'flash' && (
            <p className="text-yellow-400 text-xs mt-1">
              {item.discountPrice}€ (au lieu de {item.originalPrice}€)
            </p>
          )}
          {type === 'poll' && (
            <p className="text-purple-400 text-xs mt-1">
              {item.options?.reduce((a, o) => a + (o.votes || 0), 0) || 0} vote(s)
            </p>
          )}
          {type === 'news' && (
            <p className="text-cyan-400 text-xs mt-1">♡ {item.likes || 0} likes</p>
          )}
          {type === 'event' && (
            <p className="text-pink-400 text-xs mt-1">
              {item.date ? new Date(item.date).toLocaleDateString('fr-FR') : 'Date non définie'}
            </p>
          )}
        </div>
        <Button
          onClick={() => onDelete(item.id)}
          size="sm"
          variant="ghost"
          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// Section de contenu
const ContentSection = ({ title, icon: Icon, color, items, type, onAdd, onDelete, isLoading }) => {
  const colorClasses = {
    yellow: 'border-yellow-500/30 text-yellow-400',
    purple: 'border-purple-500/30 text-purple-400',
    cyan: 'border-cyan-500/30 text-cyan-400',
    pink: 'border-pink-500/30 text-pink-400',
  };
  const btnClasses = {
    yellow: 'bg-yellow-500 hover:bg-yellow-600 text-black',
    purple: 'bg-purple-500 hover:bg-purple-600',
    cyan: 'bg-cyan-500 hover:bg-cyan-600 text-black',
    pink: 'bg-pink-500 hover:bg-pink-600',
  };

  return (
    <div className={`bg-[#1a1a25] rounded-2xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold flex items-center gap-2 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
          {title} ({items.length})
        </h3>
        <Button onClick={onAdd} size="sm" className={btnClasses[color]}>
          <Plus className="w-4 h-4 mr-1" />
          Créer
        </Button>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Aucun contenu</p>
        ) : (
          items.map((item) => (
            <ContentCard key={item.id} item={item} type={type} onDelete={onDelete} />
          ))
        )}
      </div>
    </div>
  );
};

// Modal de création
const CreateModal = ({ isOpen, onClose, type, onCreated }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({ emoji: '🎁' });
    }
  }, [isOpen, type]);

  const typeConfig = {
    flash: { 
      title: 'Nouvelle Offre Flash',
      fields: ['title', 'originalPrice', 'discountPrice', 'emoji'],
      color: 'yellow'
    },
    poll: { 
      title: 'Nouveau Sondage',
      fields: ['question', 'options', 'emoji'],
      color: 'purple'
    },
    news: { 
      title: 'Nouvelle Actualité',
      fields: ['title', 'content', 'emoji'],
      color: 'cyan'
    },
    event: { 
      title: 'Nouvel Événement',
      fields: ['title', 'description', 'date', 'emoji'],
      color: 'pink'
    },
  };

  const config = typeConfig[type] || typeConfig.flash;

  const handleCreate = async () => {
    setIsLoading(true);
    try {
      switch (type) {
        case 'flash':
          await api.createFlashOffer?.(formData);
          break;
        case 'poll':
          const pollData = {
            ...formData,
            options: (formData.optionsText || '').split('\n').filter(o => o.trim()).map(text => ({ text, votes: 0 }))
          };
          await api.createPoll?.(pollData);
          break;
        case 'news':
          await api.createNews?.(formData);
          break;
        case 'event':
          await api.createEvent?.(formData);
          break;
      }
      toast.success('Contenu créé !');
      onCreated?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsLoading(false);
    }
  };

  const colorClasses = {
    yellow: 'border-yellow-500/30',
    purple: 'border-purple-500/30',
    cyan: 'border-cyan-500/30',
    pink: 'border-pink-500/30',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`bg-[#12121a] border ${colorClasses[config.color]}`}>
        <DialogHeader>
          <DialogTitle className="text-white">{config.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Emoji */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Emoji</label>
            <Input
              value={formData.emoji || ''}
              onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              placeholder="🎁"
              className="bg-[#1a1a25] border-gray-700 text-white w-20"
            />
          </div>

          {/* Titre / Question */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">
              {type === 'poll' ? 'Question' : 'Titre'}
            </label>
            <Input
              value={formData.title || formData.question || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                [type === 'poll' ? 'question' : 'title']: e.target.value 
              })}
              placeholder={type === 'poll' ? 'Votre question...' : 'Titre...'}
              className="bg-[#1a1a25] border-gray-700 text-white"
            />
          </div>

          {/* Champs spécifiques */}
          {type === 'flash' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Prix original (€)</label>
                <Input
                  type="number"
                  value={formData.originalPrice || ''}
                  onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                  className="bg-[#1a1a25] border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Prix réduit (€)</label>
                <Input
                  type="number"
                  value={formData.discountPrice || ''}
                  onChange={(e) => setFormData({ ...formData, discountPrice: e.target.value })}
                  className="bg-[#1a1a25] border-gray-700 text-white"
                />
              </div>
            </div>
          )}

          {type === 'poll' && (
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Options (une par ligne)</label>
              <textarea
                value={formData.optionsText || ''}
                onChange={(e) => setFormData({ ...formData, optionsText: e.target.value })}
                placeholder="Option 1\nOption 2\nOption 3"
                rows={4}
                className="w-full p-3 rounded-lg bg-[#1a1a25] border border-gray-700 text-white resize-none"
              />
            </div>
          )}

          {(type === 'news' || type === 'event') && (
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                {type === 'news' ? 'Contenu' : 'Description'}
              </label>
              <textarea
                value={formData.content || formData.description || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  [type === 'news' ? 'content' : 'description']: e.target.value 
                })}
                rows={3}
                className="w-full p-3 rounded-lg bg-[#1a1a25] border border-gray-700 text-white resize-none"
              />
            </div>
          )}

          {type === 'event' && (
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Date</label>
              <input
                type="datetime-local"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full p-2 rounded-lg bg-[#1a1a25] border border-gray-700 text-white"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 border-gray-600">
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isLoading}
              className={`flex-1 bg-gradient-to-r ${
                config.color === 'yellow' ? 'from-yellow-500 to-orange-500' :
                config.color === 'purple' ? 'from-purple-500 to-pink-500' :
                config.color === 'cyan' ? 'from-cyan-500 to-blue-500' :
                'from-pink-500 to-red-500'
              }`}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Module Content principal
const SpaceContent = () => {
  const [flashOffers, setFlashOffers] = useState([]);
  const [polls, setPolls] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createModal, setCreateModal] = useState({ open: false, type: null });

  // Charger les données
  const loadContent = async () => {
    setIsLoading(true);
    try {
      const [flashData, pollsData, newsData, eventsData] = await Promise.all([
        api.getFlashOffers?.() || { offers: [] },
        api.getActivePolls?.() || { polls: [] },
        api.getNews?.() || { news: [] },
        api.getEvents?.() || { events: [] }
      ]);
      setFlashOffers(Array.isArray(flashData) ? flashData : flashData.offers || []);
      setPolls(Array.isArray(pollsData) ? pollsData : pollsData.polls || []);
      setNews(Array.isArray(newsData) ? newsData : newsData.news || []);
      setEvents(Array.isArray(eventsData) ? eventsData : eventsData.events || []);
    } catch (error) {
      console.error('Erreur chargement contenu:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  // Supprimer un contenu
  const handleDelete = async (type, id) => {
    if (!window.confirm('Supprimer ce contenu ?')) return;
    try {
      switch (type) {
        case 'flash':
          await api.deleteFlashOffer?.(id);
          break;
        case 'poll':
          await api.deletePoll?.(id);
          break;
        case 'news':
          await api.deleteNews?.(id);
          break;
        case 'event':
          await api.deleteEvent?.(id);
          break;
      }
      toast.success('Contenu supprimé');
      loadContent();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            ⚡ CONTENU
          </h1>
          <p className="text-gray-500 text-sm">Gestion du contenu client</p>
        </div>
        <Button
          onClick={loadContent}
          variant="outline"
          className="border-yellow-500/30 text-yellow-400"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Grille de contenu */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ContentSection
          title="Offres Flash"
          icon={Zap}
          color="yellow"
          items={flashOffers}
          type="flash"
          onAdd={() => setCreateModal({ open: true, type: 'flash' })}
          onDelete={(id) => handleDelete('flash', id)}
          isLoading={isLoading}
        />
        
        <ContentSection
          title="Sondages"
          icon={BarChart3}
          color="purple"
          items={polls}
          type="poll"
          onAdd={() => setCreateModal({ open: true, type: 'poll' })}
          onDelete={(id) => handleDelete('poll', id)}
          isLoading={isLoading}
        />
        
        <ContentSection
          title="Actualités"
          icon={Newspaper}
          color="cyan"
          items={news}
          type="news"
          onAdd={() => setCreateModal({ open: true, type: 'news' })}
          onDelete={(id) => handleDelete('news', id)}
          isLoading={isLoading}
        />
        
        <ContentSection
          title="Événements"
          icon={Calendar}
          color="pink"
          items={events}
          type="event"
          onAdd={() => setCreateModal({ open: true, type: 'event' })}
          onDelete={(id) => handleDelete('event', id)}
          isLoading={isLoading}
        />
      </div>

      {/* Modal de création */}
      <CreateModal
        isOpen={createModal.open}
        onClose={() => setCreateModal({ open: false, type: null })}
        type={createModal.type}
        onCreated={loadContent}
      />
    </div>
  );
};

export default SpaceContent;
